import { format, parse, isValid } from "date-fns";

// European date format dd.MM.yyyy
export const DATE_FORMAT = "dd.MM.yyyy";
export const DATE_FORMAT_DISPLAY = "dd.MM.yyyy";

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!isValid(d)) return "";
  return format(d, DATE_FORMAT_DISPLAY);
}

export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;
  const parsed = parse(dateString, DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
}

export function formatDateRange(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
): string {
  const startStr = formatDate(start);
  const endStr = formatDate(end);
  if (startStr && endStr) {
    return `${startStr} - ${endStr}`;
  }
  if (startStr) return `From ${startStr}`;
  if (endStr) return `Until ${endStr}`;
  return "";
}

export function formatShortDateRange(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
): string {
  if (!start && !end) return "";
  if (!start) return formatDate(end!);
  if (!end) return formatDate(start!);

  const sd = typeof start === "string" ? new Date(start) : start;
  const ed = typeof end === "string" ? new Date(end) : end;
  if (!isValid(sd) || !isValid(ed)) return formatDateRange(start, end);

  const sDay = String(sd.getDate()).padStart(2, "0");
  const sMonth = String(sd.getMonth() + 1).padStart(2, "0");
  const sYear = sd.getFullYear();
  const eDay = String(ed.getDate()).padStart(2, "0");
  const eMonth = String(ed.getMonth() + 1).padStart(2, "0");
  const eYear = ed.getFullYear();

  if (sDay === eDay && sMonth === eMonth && sYear === eYear) {
    return `${sDay}.${sMonth}.${sYear}`;
  }
  if (sYear === eYear && sMonth === eMonth) {
    return `${sDay}.-${eDay}.${sMonth}.${sYear}`;
  }
  if (sYear === eYear) {
    return `${sDay}.${sMonth}.-${eDay}.${eMonth}.${sYear}`;
  }
  return `${formatDate(start)} - ${formatDate(end)}`;
}

export function formatDateForInput(
  date: string | Date | null | undefined,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!isValid(d)) return "";
  return format(d, "yyyy-MM-dd");
}

/** Count weekdays (Mon-Fri) between two YYYY-MM-DD date strings, inclusive. */
export function countBusinessDays(start: string, end: string): number {
  let count = 0;
  const d = new Date(start);
  const endDate = new Date(end);
  while (d <= endDate) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

/**
 * Count vacation days between two dates, excluding weekends and public holidays,
 * weighted by employment percentage.
 */
export function countVacationDays(
  start: string,
  end: string,
  publicHolidayDates: Set<string>,
  employmentPct: number,
): number {
  let count = 0;
  const d = new Date(start);
  const endDate = new Date(end);
  while (d <= endDate) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      const dateStr = d.toISOString().slice(0, 10);
      if (!publicHolidayDates.has(dateStr)) {
        count++;
      }
    }
    d.setDate(d.getDate() + 1);
  }
  return Math.round(count * (employmentPct / 100) * 10) / 10;
}
